"use client";

import { Carousel, CarouselContent, CarouselItem } from "@/components/rac/carousel";
import { m, useInView } from "framer-motion";
import Image from "next/image";
import Link from "next/link";
import { useRef } from "react";

interface PropertyTypeData {
	title: string;
	subtitle: string;
	imageUrl: string;
	bgColor: string;
	route: string;
}

const propertyTypes: PropertyTypeData[] = [
	{
		title: "Apartments",
		subtitle: "Find the best suitable Apartment",
		imageUrl: "/images/home/apartment.webp",
		bgColor: "from-[#4A90A4] to-[#67B5D1]",
		route: "/bengaluru/apartments",
	},
	{
		title: "House",
		subtitle: "Search vaastu compliant houses",
		imageUrl: "/images/home/house.webp",
		bgColor: "from-[#6B7280] to-[#374151]",
		route: "/bengaluru/houses",
	},
	{
		title: "Villa",
		subtitle: "Luxurious villas available for you",
		imageUrl: "/images/home/villa.webp",
		bgColor: "from-[#F59E0B] to-[#D97706]",
		route: "/bengaluru/villas",
	},
];

const PropertyTypes = () => {
	const ref = useRef(null);
	const isInView = useInView(ref, { once: true, margin: "-100px" });

	return (
		<section ref={ref} className="py-12 md:py-20 bg-muted">
			<div className="container px-4 mx-auto">
				<m.div
					initial={{ opacity: 0, y: 20 }}
					animate={isInView ? { opacity: 1, y: 0 } : {}}
					transition={{ duration: 0.6 }}
					className="text-center mb-8 md:mb-12"
				>
					<h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">Find Your Perfect Property</h2>
					<p className="text-lg text-muted-foreground max-w-2xl mx-auto">
						Discover a wide range of properties tailored to your needs and budget
					</p>
				</m.div>

				<div className="relative">
					{/* Carousel for small/medium screens */}
					<Carousel
						opts={{
							align: "center",
							loop: true,
							containScroll: "trimSnaps",
						}}
						className="lg:hidden"
					>
						<CarouselContent className="py-2 -ml-2">
							{propertyTypes.map(type => (
								<CarouselItem key={type.title} className="pl-2 basis-[75%]" aria-label={type.title}>
									<Link href={type.route}>
										<m.div
											className="group relative rounded-2xl overflow-hidden h-56 md:h-64 cursor-pointer transition-all duration-300 hover:scale-105"
											style={{ willChange: "transform" }}
										>
											{/* Background Gradient */}
											<div
												className={`absolute inset-0 bg-gradient-to-br ${type.bgColor} opacity-90 transition-opacity duration-300 group-hover:opacity-100`}
											/>

											{/* Image */}
											<div className="absolute inset-0">
												<Image
													src={type.imageUrl}
													alt={type.title}
													fill
													className="object-cover object-center transition-transform duration-300 group-hover:scale-110"
													sizes="(max-width: 768px) 75dvw, (max-width: 1024px) 50dvw, 33dvw"
													quality={65}
													loading="lazy"
												/>
											</div>

											{/* Content Overlay */}
											<div className="absolute inset-0 bg-black/20 flex flex-col justify-end">
												<div className="text-white bg-black/10 backdrop-blur-sm p-4 md:p-6">
													<h3 className="text-xl md:text-2xl font-bold mb-1 md:mb-2">{type.title}</h3>
													<p className="text-sm md:text-base opacity-90">{type.subtitle}</p>
												</div>
											</div>

											{/* Hover Effect Overlay */}
											<div className="absolute inset-0 bg-black/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
										</m.div>
									</Link>
								</CarouselItem>
							))}
						</CarouselContent>
					</Carousel>

					{/* Grid for large screens */}
					<div className="hidden lg:grid lg:grid-cols-3 lg:gap-6">
						{propertyTypes.map(type => (
							<Link href={type.route} key={type.title}>
								<m.div
									className="group relative rounded-2xl overflow-hidden h-56 md:h-64 cursor-pointer transition-all duration-300 hover:scale-105"
									style={{ willChange: "transform" }}
								>
									{/* Background Gradient */}
									<div
										className={`absolute inset-0 bg-gradient-to-br ${type.bgColor} opacity-90 transition-opacity duration-300 group-hover:opacity-100`}
									/>

									{/* Image */}
									<div className="absolute inset-0">
										<Image
											src={type.imageUrl}
											alt={type.title}
											fill
											className="object-cover object-center transition-transform duration-300 group-hover:scale-110"
											sizes="(max-width: 768px) 33dvw, (max-width: 1024px) 33dvw, 33dvw"
											quality={65}
											loading="lazy"
										/>
									</div>

									{/* Content Overlay */}
									<div className="absolute inset-0 bg-black/20 flex flex-col justify-end">
										<div className="text-white bg-black/10 backdrop-blur-sm p-4 md:p-6">
											<h3 className="text-xl md:text-2xl font-bold mb-1 md:mb-2">{type.title}</h3>
											<p className="text-sm md:text-base opacity-90">{type.subtitle}</p>
										</div>
									</div>

									{/* Hover Effect Overlay */}
									<div className="absolute inset-0 bg-black/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
								</m.div>
							</Link>
						))}
					</div>
				</div>
			</div>
		</section>
	);
};

export { PropertyTypes };
